import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CompactPicker } from 'react-color';
import { toRgb } from '../calendar/eventStyle.js';

/**
 * Colour swatch that opens a palette, rendered in a portal.
 *
 * The palette used to be positioned inside the sidebar, which scrolls: an
 * absolutely positioned child is clipped by any ancestor with overflow, so
 * half the palette disappeared behind the grid. Fixed, on document.body, no
 * ancestor can cut it — and the position is clamped to the window so it is
 * never pushed off-screen by a swatch near an edge.
 */
export default function ColorSwatchPicker({ value, onChange, label, className }) {
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const color = toRgb(value) || { r: 200, g: 200, b: 200 };

  const open = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    setAnchor({
      top: Math.min(rect.bottom + 4, window.innerHeight - PALETTE_HEIGHT - margin),
      left: Math.min(rect.left, window.innerWidth - PALETTE_WIDTH - margin),
    });
  }, []);

  useEffect(() => {
    if (!anchor) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setAnchor(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anchor]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (anchor ? setAnchor(null) : open())}
        title="Changer la couleur"
        aria-label={label}
        aria-expanded={Boolean(anchor)}
        className={className}
        style={{ backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})` }}
      />
      {anchor &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Fermer le sélecteur de couleur"
              className="fixed inset-0 z-[1500] cursor-default"
              onClick={() => setAnchor(null)}
            />
            <div
              className="fixed z-[1501]"
              style={{ top: `${anchor.top}px`, left: `${anchor.left}px` }}
            >
              <CompactPicker
                color={color}
                onChangeComplete={(picked) => {
                  onChange(picked.rgb);
                  setAnchor(null);
                }}
              />
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// CompactPicker's own dimensions, used to keep it inside the window.
const PALETTE_WIDTH = 245;
const PALETTE_HEIGHT = 100;

ColorSwatchPicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};
